import { DateYmd } from 'hydra-data-types';
import moment from 'moment';
import { DateFormat, getDateFormat } from 'ts-utils';
import { SharedScreenName } from '../../../config/FlowTypes';
import { UserDataState } from '../../../store/userData/UserDataState';

type ProcessDobOnKeyDownArgs = {
  dob: string;
  key: string;
  dateFormat?: DateFormat;
};

export function processDobOnKeyDown(args: ProcessDobOnKeyDownArgs): string {
  const { key, dob, dateFormat = getDateFormat() } = args;
  const allowedDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const isBackspace = key === 'Backspace';
  const isDigit = allowedDigits.includes(key);
  const isFullDate = dob.length === 10;
  const isMMDDYYYY = dateFormat === 'MM/DD/YYYY';

  // For MM/DD/YYYY: first digit > 1 means invalid month
  // For DD/MM/YYYY: first digit > 3 means invalid day
  const firstDigitInvalid =
    dob.length === 0 && (isMMDDYYYY ? parseInt(key, 10) > 1 : parseInt(key, 10) > 3);

  // For MM/DD/YYYY: 4th digit > 3 means invalid day
  // For DD/MM/YYYY: 4th digit > 1 means invalid month
  const fourthDigitInvalid =
    dob.length === 3 && (isMMDDYYYY ? parseInt(key, 10) > 3 : parseInt(key, 10) > 1);

  const hasOnlyEnteredTwoYearDigits = dob.length === 7;
  const isHighlightedText = window.getSelection() !== null;

  switch (true) {
    // Ignore if key is not allowed
    case !isBackspace && !isDigit:
      return dob;
    // If user selects entire input value and presses backspace, clear the input value
    case isHighlightedText && isBackspace && window.getSelection()?.toString().includes(dob):
      return '';
    // Handle backspace key
    case isBackspace:
      const shouldRemoveTwoChars = [3, 6].includes(dob.length);
      return shouldRemoveTwoChars ? dob.slice(0, -2) : dob.slice(0, -1);
    // Ignore if date is already full (as backspace processed above is only valid key when field is full)
    case isFullDate:
      return dob;
    // Auto-format first digit if invalid
    case firstDigitInvalid:
      return `0${key}/`;
    // Auto-format fourth digit if invalid
    case fourthDigitInvalid:
      return `${dob}0${key}/`;
    // Check if the last two digits of dob does not equal 19 or 20
    case hasOnlyEnteredTwoYearDigits:
      const lastTwoDigits = dob.slice(-1) + key;
      const year = parseInt(lastTwoDigits, 10);
      // if the year is 19 or 20, assume user is entering full year and return dob
      if (year === 19 || year === 20) {
        return dob + key;
      } else if (year <= 18) {
        // If the year is less than or equal to 18, assume it is 20xx as they'll be over 100 years old
        return `${dob.slice(0, -2)}/20${lastTwoDigits}`;
      } else {
        // If the year is greater than 18, assume it is 19xx
        return `${dob.slice(0, -2)}/19${lastTwoDigits}`;
      }
    default:
      const newDob = dob + key;
      const shouldAddSlash = [2, 5].includes(newDob.length);
      return shouldAddSlash ? `${newDob}/` : newDob;
  }
}

type GetDobErrorArgs = {
  dobUserInputCurrent: string;
  minimumAge?: number;
  productName?: string;
  dateFormat?: DateFormat;
};

export function getDobError(args: GetDobErrorArgs): string {
  const {
    minimumAge = 13,
    dobUserInputCurrent,
    productName = 'Nerva',
    dateFormat = getDateFormat(),
  } = args;
  let dobError = '';
  if (dobUserInputCurrent.length === 10) {
    const newDate = moment(dobUserInputCurrent, dateFormat);
    const formatLower = dateFormat.toLowerCase();
    if (!newDate.isValid()) {
      dobError = `Please enter a valid date in the ${formatLower} format.`;
    } else if (moment().diff(newDate, 'years') < minimumAge) {
      dobError = `You must be at least ${minimumAge} years old to use ${productName}.`;
    }
  }
  return dobError;
}

/**
 * What the date-of-birth screen saves. The patient's own date of birth also stores their age, which
 * the Segment traits and every US 65+ routing decision read.
 */
export function getDateOfBirthUserData(screenName: string, value: DateYmd): UserDataState {
  if (screenName !== SharedScreenName.DateOfBirth) return { [screenName]: [value] };
  const age = moment().diff(moment(value, 'yyyy-MM-DD'), 'years');
  return { [screenName]: [value], age: [age.toString()] };
}
